import { Component } from 'react';

/**
 * ChunkLoadErrorBoundary
 * ----------------------
 * After a fresh deploy, returning users still have the *old* index.html
 * cached, which references chunk filenames (e.g. `Pricing.abc123.js`) that
 * no longer exist on the server. When React tries to lazy-load that chunk,
 * import() throws a ChunkLoadError / "Loading chunk N failed" — Suspense
 * never resolves, the page goes blank, and the user's Back button gets
 * stuck on an unmounted route.
 *
 * This boundary catches that specific class of error and *force-reloads*
 * the page once (with a query-string bust) so the browser fetches the new
 * index.html and the correct chunk filenames. A `?cb=` flag prevents an
 * infinite reload loop if the failure is something else.
 */
const CHUNK_ERR_RE = /Loading chunk|ChunkLoadError|Loading CSS chunk|Failed to fetch dynamically imported module/i;

export class ChunkLoadErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, message: '' };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, message: error?.message || String(error) };
  }

  componentDidCatch(error) {
    if (error && CHUNK_ERR_RE.test(error.message || '')) {
      const url = new URL(window.location.href);
      // Only auto-reload once per visit to avoid loops on real errors.
      if (!url.searchParams.has('cb')) {
        url.searchParams.set('cb', String(Date.now()));
        window.location.replace(url.toString());
      }
    }
  }

  render() {
    if (!this.state.hasError) return this.props.children;
    return (
      <div
        data-testid="chunk-error-fallback"
        className="min-h-screen hero-gradient flex items-center justify-center px-6 text-center"
      >
        <div className="max-w-md space-y-6">
          <p className="text-[10px] uppercase tracking-[0.4em] text-[var(--accent-violet)]">
            A glitch on the path
          </p>
          <h1 className="font-['Cormorant_Garamond'] text-3xl md:text-4xl text-[var(--text-primary)] glow-text-gold">
            Refreshing the sanctuary…
          </h1>
          <p className="text-sm text-[var(--text-secondary)] leading-relaxed">
            Your browser was holding an older version of the site. Tap below to load the latest.
          </p>
          <button
            data-testid="chunk-error-reload-btn"
            onClick={() => window.location.replace(window.location.pathname + window.location.search)}
            className="cinematic-cta px-8 py-3 rounded-full text-[var(--text-accent)] text-xs uppercase tracking-[0.25em]"
          >
            Reload the page
          </button>
        </div>
      </div>
    );
  }
}

export default ChunkLoadErrorBoundary;
